// Chemin: C:\gestion-copro\src\components\conseil-syndical\LierCompteModal.tsx
// Modal pour créer un compte utilisateur lié à un membre CS ou copropriétaire
// Session 57 : Fonctionnalité "Lier un compte"
// Session 73 : Ajout envoi automatique des identifiants par email
// Session 79 : Correction affichage selon emailSent (email envoyé ou non)

import React, { useState, useEffect } from 'react';
import { 
  X, 
  UserPlus, 
  Mail, 
  Key, 
  RefreshCw, 
  Copy, 
  Check, 
  AlertCircle,
  CheckCircle,
  Send
} from 'lucide-react';
import { 
  createLinkedUserAccount, 
  generateTemporaryPassword, 
  isValidEmail,
  CreateUserAccountParams 
} from '../../services/user.service';

// ============================================
// TYPES
// ============================================

interface LierCompteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  membre: {
    id: string;
    nom: string;
    prenom: string;
    email?: string;
  };
  membreCollection: 'membresCS' | 'coproprietaires';
  coproprieteId: string;
  role: 'cs' | 'coproprietaire';
}

type ModalStep = 'form' | 'success';

// ============================================
// COMPOSANT
// ============================================

const LierCompteModal: React.FC<LierCompteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  membre,
  membreCollection,
  coproprieteId,
  role
}) => {
  // États du formulaire
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<ModalStep>('form');
  
  // États UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [createdEmail, setCreatedEmail] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');
  
  // Session 79 : État pour savoir si l'email a été envoyé
  const [emailSent, setEmailSent] = useState(false);

  // Initialiser le formulaire à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setEmail(membre.email || '');
      setPassword(generateTemporaryPassword());
      setStep('form');
      setError(null);
      setCopied(false);
      setEmailSent(false);
    }
  }, [isOpen, membre.email]);

  // Régénérer le mot de passe
  const handleRegeneratePassword = () => {
    setPassword(generateTemporaryPassword());
  };

  // Copier dans le presse-papier
  const handleCopyCredentials = async () => {
    const text = `Email: ${createdEmail}\nMot de passe: ${createdPassword}`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Erreur copie:', err);
    }
  };

  // Créer le compte
  const handleCreate = async () => {
    // Validation
    if (!email.trim()) {
      setError('L\'adresse email est obligatoire');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Adresse email invalide');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: CreateUserAccountParams = {
        email: email.trim(),
        password,
        nom: membre.nom,
        prenom: membre.prenom,
        role,
        coproprieteId,
        membreId: membre.id,
        membreCollection
      };

      // Session 79 : Récupérer la réponse complète de la function
      const result = await createLinkedUserAccount(params);

      // Succès - passer à l'écran de confirmation
      setCreatedEmail(email.trim());
      setCreatedPassword(password);
      setEmailSent(result.emailSent); // Session 79 : Stocker le statut d'envoi
      setStep('success');
      onSuccess();

    } catch (err: any) {
      console.error('Erreur création compte:', err);
      
      // Gérer les erreurs Firebase
      let message = 'Erreur lors de la création du compte';
      
      if (err.code === 'functions/already-exists') {
        message = err.message || 'Un compte existe déjà avec cette adresse email';
      } else if (err.code === 'functions/invalid-argument') {
        message = err.message || 'Données invalides';
      } else if (err.code === 'functions/permission-denied') {
        message = 'Vous n\'avez pas les droits pour créer des comptes';
      } else if (err.message) {
        message = err.message;
      }
      
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Fermer la modal
  const handleClose = () => {
    setStep('form');
    setError(null);
    setEmailSent(false);
    onClose();
  };

  if (!isOpen) return null;

  const roleLabel = role === 'cs' ? 'membre du conseil syndical' : 'copropriétaire';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* En-tête */}
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">
              {step === 'form' ? 'Créer un accès' : 'Compte créé'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-4">
          {step === 'form' ? (
            <>
              {/* Info membre */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600">
                  Créer un compte pour :
                </p>
                <p className="font-semibold text-gray-900">
                  {membre.prenom} {membre.nom}
                </p>
                <p className="text-sm text-blue-600">
                  Rôle : {roleLabel}
                </p>
              </div>

              {/* Message d'erreur */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Formulaire */}
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Adresse email *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="exemple@email.com"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {!membre.email && (
                    <p className="text-xs text-amber-600 mt-1">
                      Aucun email enregistré pour ce membre
                    </p>
                  )}
                </div>

                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Key className="w-4 h-4 inline mr-1" />
                    Mot de passe temporaire
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleRegeneratePassword}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Régénérer"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Le membre pourra changer ce mot de passe après sa première connexion
                  </p>
                </div>
              </div>
            </>
          ) : (
            /* Écran de succès - Session 79 : Affichage conditionnel selon emailSent */
            <>
              <div className="text-center mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  emailSent ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  {emailSent ? (
                    <Send className="w-8 h-8 text-green-600" />
                  ) : (
                    <CheckCircle className="w-8 h-8 text-amber-600" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Compte créé avec succès !
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {membre.prenom} {membre.nom} peut maintenant se connecter
                </p>
              </div>

              {/* Session 79 : Affichage conditionnel selon emailSent */}
              {emailSent ? (
                /* Email envoyé avec succès */
                <div className="flex items-start gap-3 bg-green-50 text-green-800 p-4 rounded-lg">
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Email envoyé !</p>
                    <p className="text-sm mt-1">
                      Les identifiants de connexion ont été envoyés à :
                    </p>
                    <p className="font-mono text-sm mt-1 font-semibold">
                      {createdEmail}
                    </p>
                  </div>
                </div>
              ) : (
                /* Email NON envoyé - afficher les identifiants manuellement */
                <>
                  {/* Alerte email non envoyé */}
                  <div className="flex items-start gap-2 bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Email non envoyé</p>
                      <p className="text-xs mt-1">
                        L'email automatique n'a pas pu être envoyé. Veuillez communiquer les identifiants manuellement.
                      </p>
                    </div>
                  </div>

                  {/* Identifiants */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-500">Email :</span>
                        <p className="font-mono text-sm">{createdEmail}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Mot de passe :</span>
                        <p className="font-mono text-sm font-semibold">{createdPassword}</p>
                      </div>
                    </div>
                  </div>

                  {/* Bouton copier */}
                  <button
                    onClick={handleCopyCredentials}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      copied 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copié !
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copier les identifiants
                      </>
                    )}
                  </button>

                  {/* Avertissement */}
                  <div className="flex items-start gap-2 bg-amber-50 text-amber-700 p-3 rounded-lg mt-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      Communiquez ces identifiants au membre de façon sécurisée.
                      Le mot de passe ne sera plus visible après fermeture de cette fenêtre.
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          {step === 'form' ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Créer le compte
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LierCompteModal;
